<template>
  <section class="cstcloud-page">
    <article class="cstcloud-card cstcloud-form-card">
      <div class="cstcloud-brand"><span>CST</span> 中国科技云</div>
      <h1>完善本站账号资料</h1>
      <p class="cstcloud-lead">科技云已完成身份认证。带“科技云预填”的内容仍请您核对，提交前不会创建本站账号。</p>
      <div v-if="expired" class="service-notice warning">本次办理凭据已失效，请重新发起科技云登录。</div>
      <form v-else class="cstcloud-form" @submit.prevent="submit">
        <label>真实姓名 <em>*</em><small v-if="source.name">科技云预填</small><input v-model.trim="form.name" maxlength="50" /></label>
        <fieldset>
          <legend>性别 <em>*</em></legend>
          <label><input v-model="form.gender" type="radio" value="男" /> 男</label>
          <label><input v-model="form.gender" type="radio" value="女" /> 女</label>
        </fieldset>
        <label class="wide">电子邮箱 <em>*</em><small v-if="emailVerified">科技云已验证</small><input v-model.trim="form.email" type="email" :readonly="emailVerified" /></label>
        <div v-if="!emailVerified" class="cstcloud-code-field wide">
          <label>本站邮箱验证码 <em>*</em><input v-model.trim="form.verificationCode" inputmode="numeric" maxlength="6" /></label>
          <button type="button" class="ghost-action compact" :disabled="codeSending || codeSeconds > 0" @click="sendCode">{{ codeButtonText }}</button>
        </div>
        <label>工作/学习单位 <em>*</em><small v-if="source.unit">科技云预填</small><input v-model.trim="form.unit" maxlength="200" /></label>
        <label>学校/工作单位税号 <em>*</em><input v-model.trim="form.orgCreditCode" maxlength="32" /></label>
        <label>职务类型 <em>*</em><select v-model="form.role"><option>教师</option><option>学生</option><option>嘉宾</option></select></label>
        <label>职称<input v-model.trim="form.title" maxlength="50" /></label>
        <label class="cstcloud-check wide"><input v-model="form.agreementAccepted" type="checkbox" /> <span>我已阅读并同意用户协议</span></label>
        <label class="cstcloud-check wide"><input v-model="form.privacyAccepted" type="checkbox" /> <span>我已阅读并同意隐私政策</span></label>
        <p v-if="feedback" class="service-notice wide" :class="{ warning: failed }" role="alert">{{ feedback }}</p>
        <div class="cstcloud-actions wide">
          <button type="submit" class="primary-action" :disabled="submitting">{{ submitting ? '正在创建...' : '创建账号并登录' }}</button>
          <button type="button" class="ghost-action" @click="$router.push('/auth/cstcloud/bind-existing')">绑定已有账号</button>
        </div>
      </form>
    </article>
  </section>
</template>

<script>
import { registerCstCloudUser, saveToken, sendEmailCode } from '../../api/paleo'
import { clearOnboardingFlow, readOnboardingFlow, safeRedirectPath, unwrapApiData } from '../../utils/cstCloudAuth'

export default {
  name: 'CstCloudOnboardingPage',
  inject: ['front'],
  data() {
    return {
      flow: null,
      expired: false,
      submitting: false,
      codeSending: false,
      codeSeconds: 0,
      codeTimer: null,
      feedback: '',
      failed: false,
      source: {},
      emailVerified: false,
      form: { name: '', gender: '', email: '', verificationCode: '', unit: '', orgCreditCode: '', role: '教师', title: '', agreementAccepted: false, privacyAccepted: false }
    }
  },
  computed: {
    codeButtonText() { return this.codeSeconds > 0 ? `${this.codeSeconds}s 后重发` : (this.codeSending ? '发送中...' : '获取验证码') }
  },
  created() {
    this.flow = readOnboardingFlow()
    if (!this.flow) { this.expired = true; return }
    const profile = this.flow.profile || {}
    this.source = { name: profile.name || profile.realName, unit: profile.unit || profile.organization }
    this.emailVerified = profile.emailVerified === true || profile.emailVerified === '1'
    this.form = {
      ...this.form,
      name: profile.name || profile.realName || '',
      gender: profile.gender || '',
      email: profile.email || '',
      unit: profile.unit || profile.organization || '',
      title: profile.title || '',
      role: profile.role || '教师'
    }
  },
  beforeDestroy() { if (this.codeTimer) clearInterval(this.codeTimer) },
  methods: {
    validEmail(value) { return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value) },
    async sendCode() {
      if (!this.validEmail(this.form.email)) { this.showError('请先填写正确的电子邮箱'); return }
      this.codeSending = true
      try {
        await sendEmailCode(this.form.email.toLowerCase(), 'CSTCLOUD_REGISTER')
        this.feedback = '验证码已发送，请查收邮箱'
        this.failed = false
        this.codeSeconds = 60
        this.codeTimer = setInterval(() => { this.codeSeconds -= 1; if (this.codeSeconds <= 0) { clearInterval(this.codeTimer); this.codeTimer = null } }, 1000)
      } catch (error) { this.showError(error.message || '验证码发送失败') } finally { this.codeSending = false }
    },
    validate() {
      if (!this.form.name || !this.form.gender || !this.validEmail(this.form.email) || !this.form.unit || !this.form.orgCreditCode || !this.form.role) return '请完整填写所有必填资料'
      if (!this.emailVerified && !/^\d{6}$/.test(this.form.verificationCode)) return '请输入 6 位本站邮箱验证码'
      if (!this.form.agreementAccepted || !this.form.privacyAccepted) return '请阅读并同意用户协议和隐私政策'
      return ''
    },
    async submit() {
      if (this.submitting) return
      const error = this.validate()
      if (error) { this.showError(error); return }
      this.submitting = true
      try {
        const data = unwrapApiData(await registerCstCloudUser({
          onboardingTicket: this.flow.onboardingTicket,
          ...this.form,
          email: this.form.email.toLowerCase(),
          emailVerificationCode: this.emailVerified ? undefined : this.form.verificationCode,
          userAgreementVersion: 'current',
          privacyPolicyVersion: 'current'
        }))
        if (!data.token) throw new Error('账号已创建，但登录凭证签发失败，请重新登录')
        clearOnboardingFlow()
        saveToken(data.token)
        this.front.isLoggedIn = true
        this.front.applyMembershipSession(data)
        await this.front.loadSelfService()
        await this.$router.replace(safeRedirectPath(data.redirectPath || this.flow.redirectPath, '/')).catch(() => {})
      } catch (error) { this.showError(error.message || '账号创建失败，请稍后重试') } finally { this.submitting = false }
    },
    showError(message) { this.feedback = message; this.failed = true }
  }
}
</script>
